import { createApi } from '@reduxjs/toolkit/query/react';
import { filteredFilter } from '../../utils/api';
import {properties} from "../../properties.js";
import { authBaseQuery } from '../../app/axiosBaseQuery';
import { PaginationResult, ListRequest } from '../../types/global.d';
import * as Entity from '../types/models/RoadCarrier';
import {createNormalizationMiddleware} from "../../app/normy/normyRtkQuery.ts";

export const ROAD_CARRIER_API_REDUCER_KEY = 'road-carriers';
export const ROAD_CARRIER_PATH = 'road-carriers';

export const roadCarrierApi = createApi({
    reducerPath: ROAD_CARRIER_API_REDUCER_KEY,
    baseQuery: authBaseQuery(),
    keepUnusedDataFor: 10,
    refetchOnMountOrArgChange: 10,
    refetchOnFocus: true,
    tagTypes: ['RoadCarrier', 'RoadCarriers'],
    endpoints: (builder) => ({
        getRoadCarrierList: builder.query<PaginationResult<Entity.RoadCarrier>, ListRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request))
                return ({
                    url: `/${ ROAD_CARRIER_PATH }?${new URLSearchParams(queryParams).toString()}`,
                    method: 'GET',
                })
            },
            providesTags: ['RoadCarriers'],
        }),
        getRoadCarrier: builder.query<Entity.RoadCarrier, Entity.PrimaryRoadCarrier>({
            queryFn: async (id, { getState }, extraOptions, baseQuery) => {

                const cachedData = roadCarrierNormalizer.getObjectById(id.id);

                if (cachedData) {
                    return { data: cachedData };
                } else {
                    const response = await baseQuery({
                        url: `/${ ROAD_CARRIER_PATH }/${id.toPath()}`,
                        method: 'GET',
                    });

                    return response;
                }
            },
            providesTags: ['RoadCarrier'],
        }),
        getRoadCarrierMultiple: builder.query<Entity.RoadCarrier, Entity.PrimaryRoadCarrier[]>({
            query: (ids) => {
                return ({
                    url: `/${ ROAD_CARRIER_PATH }/ids${Entity.PrimaryRoadCarrier.toQueryParams(ids)}`,
                    method: 'GET',
                })
            },
            providesTags: ['RoadCarriers'],
        }),
        getRoadCarrierPresentation: builder.query<PaginationResult<Entity.RoadCarrier>, Entity.PresentationRoadCarrierRequest>({
            query: (request) => {
              const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
              return ({
                url: `/${ ROAD_CARRIER_PATH }/presentation?${new URLSearchParams(queryParams).toString()}`,
                method: 'POST',
                data: request.filter,
              })
            },
            providesTags: ['RoadCarriers'],
        }),
        
        insertRoadCarrier: builder.mutation<Entity.RoadCarrier, Entity.CreateRoadCarrier>({
            query: (toInsert) => {
                return ({
                    url: `/${ ROAD_CARRIER_PATH }`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                dispatch(
                    roadCarrierApi.util.upsertQueryData('getRoadCarrier', Entity.PrimaryRoadCarrier.fromId(mutationData.id), mutationData),
                );
            },
            invalidatesTags: ['RoadCarriers'],
        }),
        insertMultipleRoadCarrier: builder.mutation<Entity.RoadCarrier[], Entity.CreateRoadCarrier[]>({
            query: (toInsert) => {
                return ({
                    url: `/${ ROAD_CARRIER_PATH }/batch`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                for(const item of mutationData) {
                    dispatch(
                            roadCarrierApi.util.upsertQueryData('getRoadCarrier', Entity.PrimaryRoadCarrier.fromId(item.id), item),
                    );
                }
            },
            invalidatesTags: ['RoadCarriers'],
        }),
        updateRoadCarrier: builder.mutation<Entity.RoadCarrier, Entity.UpdateEntityRoadCarrier>({
            query: (toUpdate) => {
                return ({
                    url: `/${ ROAD_CARRIER_PATH }/${toUpdate.id.toPath()}`,
                    method: 'PUT',
                    data: toUpdate.data,
                })
            },
            invalidatesTags: ['RoadCarriers'],
        }),
        updateMultipleRoadCarrier: builder.mutation<Entity.RoadCarrier[], Entity.UpdateEntityRoadCarrier[]>({
            query: (toUpdate) => {
                return ({
                    url: `/${ ROAD_CARRIER_PATH }/batch`,
                    method: 'PUT',
                    data: toUpdate,
                })
            },
            invalidatesTags: ['RoadCarriers'],
        }),
        deleteRoadCarrier: builder.mutation<Entity.RoadCarrier, Entity.RoadCarrier>({
            query: (toDelete) => {
                return ({
                    url: `/${ ROAD_CARRIER_PATH }/${Entity.PrimaryRoadCarrier.fromEntity(toDelete)?.toPath()}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['RoadCarriers'],
        }),
        deleteMultipleRoadCarrier: builder.mutation<Entity.RoadCarrier[], Entity.PrimaryRoadCarrier[]>({
            query: (ids) => {
                return ({
                    url: `/${ ROAD_CARRIER_PATH }/batch${Entity.PrimaryRoadCarrier.toQueryParams(ids)}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['RoadCarriers'],
        }),
    }),
})

export const [roadCarrierNormalizer, roadCarrierNormalizerMiddleware] = createNormalizationMiddleware(ROAD_CARRIER_API_REDUCER_KEY, roadCarrierApi, {
    devLogging: properties.debugLog,
})

export const {
    useGetRoadCarrierListQuery,
    useGetRoadCarrierMultipleQuery,
    useGetRoadCarrierPresentationQuery,
    useGetRoadCarrierQuery,
    useInsertRoadCarrierMutation,
    useInsertMultipleRoadCarrierMutation,
    useUpdateRoadCarrierMutation,
    useUpdateMultipleRoadCarrierMutation,
    useDeleteRoadCarrierMutation,
    useDeleteMultipleRoadCarrierMutation,
    
} = roadCarrierApi
